import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { getCohortBuilderPath } from '@verantos-dev/core-constants';
import { cohortApiSlice, useCreateCohortMutation } from '@verantos-dev/data-access-cohorts';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';
import { type CreateCohortWithDatasetData } from '@verantos-dev/types-cohorts';

import { useCohortModalContext } from '../context/modalContext';

export const useCreateCohortModal = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  // local state for model entry
  const [cohortName, setCohortName] = useState('');

  // context for modal state
  const { closeDialog } = useCohortModalContext();

  // projects query hook
  const { data: _data, error: _error } = useGetProjectQuery(projectId || '');

  // Cohort creation mutation hook
  const [
    createCohort,
    { isLoading: isCreateCohortLoading, error: createCohortError, data: createCohortResponse },
  ] = useCreateCohortMutation();

  // handle cohort name change
  const onChangeCohortName = (_: string, value: string) => {
    setCohortName(value);
  };

  // handle form submission
  const onSubmit = async () => {
    const cohortNameTrimmed = cohortName?.trim();
    if (!cohortNameTrimmed) {
      return;
    }

    try {
      const createCohortData: CreateCohortWithDatasetData = {
        cohortName: cohortNameTrimmed,
        cohortDescription: '',
        datasetReleaseId: _data?.data.datasetReleaseId || '',
      };
      await createCohort({
        projectId: projectId || '',
        data: createCohortData,
      });
    } catch (error) {
      console.error('Error creating cohort', error);
    }
  };

  // handle redirect after successful
  const onRedirectAfterSubmit = () => {
    const cohortId = createCohortResponse?.data?.cohortProfileId;
    const cohortVersionId = createCohortResponse?.data?.latestCohortVersionId;
    if (projectId && cohortId && cohortVersionId) {
      cohortApiSlice.endpoints.getCohort.initiate({ projectId, cohortId });
      cohortApiSlice.endpoints.getCohortVersion.initiate({ projectId, cohortId, cohortVersionId });
      navigate(`${getCohortBuilderPath(projectId, cohortId, cohortVersionId)}?pageTab=events`);
    }
  };

  // messages for the modal content area
  const createCohortModalContentMessages = {
    cohortNameLabel: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cohortName.label',
    }),
    cohortNamePlaceholder: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cohortName.placeholder',
    }),
    datasetLabel: intl.formatMessage({ id: 'cohortsList.createCohortModal.dataset.label' }),
    datasetPlaceholder: intl.formatMessage({
      id: 'cohortsList.createCohortModal.dataset.placeholder',
    }),
    maxCharacterErrorMsg: intl.formatMessage({
      id: 'cohortsList.createCohortModal.maxCharacterErrorMsg',
    }),
  };

  // message overrides for the api workflow dialog
  const apiWorkflowDialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cancelButton',
    }),
    loadingSaveButtonText: intl.formatMessage({ id: 'cohortsList.createCohortModal.saveButton' }),

    entryCancelButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cancelButton',
    }),
    entrySaveButtonText: intl.formatMessage({ id: 'cohortsList.createCohortModal.saveButton' }),

    submittingCancelButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cancelButton',
    }),
    submittingSaveButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.submitting',
    }),

    successCloseButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.closeButton',
    }),

    submittingErrorTitle: intl.formatMessage({
      id: 'cohortsList.createCohortModal.submittingError',
    }),

    successTitle: intl.formatMessage({ id: 'cohortsList.createCohortModal.success.title' }),
    successText: intl.formatMessage(
      { id: 'cohortsList.createCohortModal.success.text' },
      { cohortName: cohortName }
    ),
  };

  return {
    title: intl.formatMessage({ id: 'cohortsList.createCohortModal.title' }),
    closeCreateCohortModal: closeDialog,
    createCohortModalContentMessages,
    apiWorkflowDialogMessages,
    _error,
    releases: _data?.data,
    cohortName,
    onChangeCohortName,
    onSubmit,
    onRedirectAfterSubmit,
    isCreateCohortLoading,
    createCohortError: createCohortError,
    isValidToSubmit: Boolean(cohortName?.trim()),
  };
};
